import React from 'react';
import clsx from 'clsx';

export type ToggleSwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  labelPosition?: 'left' | 'right';
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
};

export function ToggleSwitch({
  checked,
  onChange,
  label,
  labelPosition = 'left',
  size = 'md',
  disabled = false,
  className
}: ToggleSwitchProps) {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const sizes = {
    sm: {
      container: 'h-4 w-7',
      knob: 'h-3 w-3',
      knobTranslate: checked ? 'translate-x-[14px]' : 'translate-x-0.5',
      text: 'text-xs'
    },
    md: {
      container: 'h-5 w-9',
      knob: 'h-4 w-4',
      knobTranslate: checked ? 'translate-x-[18px]' : 'translate-x-0.5',
      text: 'text-sm'
    }
  };

  const s = sizes[size];

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={handleClick}
      className={clsx(
        'relative rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
        s.container,
        checked ? 'bg-gray-800' : 'bg-gray-300',
        disabled && 'cursor-not-allowed opacity-50'
      )}>
      <span
        className={clsx(
          'absolute top-0.5 flex items-center justify-center rounded-full bg-white shadow transition-transform',
          s.knob,
          s.knobTranslate
        )}>
        {checked && (
          <svg
            className="h-2.5 w-2.5 text-gray-800"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
    </button>
  );

  if (!label) {
    return toggle;
  }

  return (
    <div
      className={clsx(
        'inline-flex cursor-pointer items-center gap-2 overflow-hidden border border-gray-200 bg-white py-0.5',
        labelPosition === 'right' ? 'pl-0.5 pr-2' : 'pl-2 pr-0.5',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      style={{
        borderRadius: labelPosition === 'right' ? '16px 8px 8px 16px' : '8px 16px 16px 8px'
      }}
      onClick={handleClick}>
      {labelPosition === 'left' && (
        <span className={clsx('font-medium text-gray-700', s.text)}>{label}</span>
      )}
      {toggle}
      {labelPosition === 'right' && (
        <span className={clsx('font-medium text-gray-700', s.text)}>{label}</span>
      )}
    </div>
  );
}
